import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { RhythmPatternPlayer, TRESILLO } from "@/components/mt/RhythmPatternPlayer";
import { NextUp } from "@/components/Callouts";
import type { RhythmPattern } from "@/components/mt/RhythmPatternPlayer";

/* Slow tresillo (eighth-note resolution, three hits per bar) — 3+3+2 in 8ths.
   In the 16-cell (16th-note) grid: beat 1 = cell 0, &-of-2 = cell 6, beat 4 = cell 12. */
const TRESILLO_SLOW: RhythmPattern = {
  name: "Slow Tresillo (eighths)",
  description:
    "Counted as '1 and 2 and 3 and 4 and'. Three hits per bar: on 1, on the '&' of 2, and on 4. Classic 3+3+2 spacing — the gentle, groovy version that powers pop and reggaeton.",
  cells: ["K","","","","","","K","","","","","","K","","",""],
  bpm: 96,
};

/* Fast tresillo (sixteenth-note resolution). Hit points: 1, the 'a' of 1
   (cell 3), then the '&' of 2 (cell 6). Repeats from beat 3: cell 8, 11, 14. */
const TRESILLO_FAST: RhythmPattern = {
  name: "Fast Tresillo (sixteenths)",
  description:
    "Counted as '1 e and a, 2 e and a, 3 e and a, 4 e and a'. Three hits per half-bar: on 1, on the 'a' of 1, and on the '&' of 2 — then the same shape repeats from beat 3. Dancier, tighter coordination.",
  cells: ["K","","","K","","","K","","K","","","K","","","K",""],
  bpm: 100,
};

/* Modified tresillo — slow tresillo with the third hit pulled one 16th earlier:
   from beat 4 (cell 12) to the 'a' of beat 3 (cell 11). */
const TRESILLO_MODIFIED: RhythmPattern = {
  name: "Modified Tresillo",
  description:
    "Slow tresillo with the third hit pulled back by one 16th: instead of landing squarely on beat 4, it lands on the 'a' of beat 3. Slightly more forward-leaning — used to break up repetitive loops.",
  cells: ["K","","","","","","K","","","","","K","","","",""],
  bpm: 96,
};

export default function MusicTheory17() {
  const { lesson, course, accent } = findLesson("music-theory", "17");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT17-Tresillo-Animated.mp4"
        caption="Tresillo — the 3+3+2 groove on a beat grid"
        hint="≈ 42 sec · watch first"
      />
      <Chapter number="17" eyebrow="Music Theory · Chapter Seventeen" title="The rhythm at the heart of modern pop">
        <Prose>
          <p>
            For sixteen chapters we've worked on pitch — scales, chord, intervals, key
            signatures. Music has another axis though, just as decisive: <strong>time</strong>.
            And of all the rhythmic patterns that have shaped contemporary popular music,
            none has spread more aggressively than the one we're about to learn. From
            reggaeton to Drake to dembow to almost every chart hit of the last fifteen
            years, the same three-hit pattern rides under the surface. Its name is the
            <strong> Tresillo</strong>.
          </p>
          <p>
            Tresillo comes from the Spanish word for "three", and that's exactly what it
            is: <em>three hit points</em> arranged across a bar of four beats, in a
            way that knocks the listener slightly off the steady pulse. The first hit
            sits squarely on beat 1. The second hit lands not on beat 2 but on the
            <em> off-beat between</em> 2 and 3. The third hit lands on beat 4. Three
            hits, four beats, and the gap between the second and third hits creates the
            distinctive lurch that makes the body want to move.
          </p>
        </Prose>
        <MTQuote>
          That <em>and</em> of the two creates a lot of excitement for the body in general,
          because it's against the normality of the pulse.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The pulse and the subdivision" title="Counting before playing">
        <Prose>
          <p>
            Before we hit the tresillo on an instrument, we need to count it. Every
            rhythmic pattern lives on top of a steady pulse — the underlying beat, the
            tick of the metronome. Tresillo lives on a four-beat bar, so the pulse is
            "<strong>1, 2, 3, 4</strong>" repeating.
          </p>
          <p>
            But the tresillo doesn't only land on the numbered beats — it has hits
            <em> between</em> them. To name those between-spots, we subdivide the beat.
            Splitting each beat into two equal halves gives us "<strong>1 and 2 and 3
            and 4 and</strong>" — eight equal slots per bar. That's the <em>slow
            tresillo</em> resolution. Splitting each beat into four equal quarters gives
            us "<strong>1 e and a, 2 e and a, 3 e and a, 4 e and a</strong>" — sixteen
            slots per bar. That's the <em>fast tresillo</em> resolution.
          </p>
          <p>
            The "and" can be played evenly (a perfectly straight subdivision) or with a
            slight delay (a <em>swung</em> subdivision, common in jazz and blues). For
            tresillo we typically play it straight, but the lesson sits in the same
            family as the swung shuffles you'll meet in later rhythm chapters.
          </p>
        </Prose>

        <TheoryBox label="Slow vs fast — same shape, different lens">
          <strong>Slow tresillo</strong> (eighth-note resolution): three hits at
          <em> 1, &-of-2, 4</em>. Counted "1 <em>and</em> 2 <em>and</em> 3 <em>and</em> 4
          <em> and</em>". <br/>
          <strong>Fast tresillo</strong> (sixteenth-note resolution): three hits at
          <em> 1, a-of-1, &-of-2</em> — then the same shape repeats from beat 3.
          Counted "1 e <em>and</em> a, 2 e <em>and</em> a, 3 e <em>and</em> a, 4 e
          <em> and</em> a". <br/>
          The tempo doesn't change; the hits are just spaced more closely.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The slow tresillo" title="Three hits at 1, the 'and' of 2, and 4">
        <Prose>
          <p>
            The slow tresillo is the more common of the two — it's the one you hear in
            "Despacito", in "One Dance", in countless reggaeton and Latin pop tracks.
            Played as eighth notes, the rhythm is sparse and groovy: a hit, a gap, a
            hit, a gap, a hit, then back to the top. It's a <strong>3-3-2</strong>
            pattern of eighth-note distances: from hit 1 to hit 2 is three eighth notes,
            from hit 2 to hit 3 is three eighth notes, and from hit 3 around back to hit
            1 of the next bar is two eighth notes.
          </p>
          <p>
            Try clapping along while saying the count out loud: "1 <em>and</em> 2
            <em> and</em> 3 <em>and</em> 4 <em>and</em>". Clap on the 1, on the
            <em> and-of-2</em>, and on the 4. Repeat. After a few rounds your body
            should start finding the dance feel — that's the whole point.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[TRESILLO_SLOW]} />

        <Prose>
          <p>
            On the piano, a beautiful exercise is to <strong>contrast the pulse with the
            tresillo</strong>. Use your left hand to keep the steady pulse — say, a low
            B on every beat, "1, 2, 3, 4". Use your right hand to play the tresillo —
            another B, but only on the three hit points. The right hand swings against
            the left's straight pulse. This is your introduction to <em>hand
            independence</em>, the bedrock skill of all groove playing.
          </p>
          <p>
            Once that feels stable, move beyond a single note. Play the tresillo over a
            scale — say B Dorian (B, C♯, D, E, F♯, G♯, A) — climbing one note higher on
            each tresillo hit. Then move it under a chord progression: B minor, G major,
            D major, A major, holding each chord for a bar while the tresillo rides on
            top. The pattern stays the same; the harmony changes underneath.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The fast tresillo" title="Three hits per half-bar, on sixteenths">
        <Prose>
          <p>
            The fast tresillo doubles the resolution. Now we're working in sixteenth
            notes — sixteen cells per bar — and the three hits land on <em>1, the "a"
            of 1, and the "and" of 2</em>. Then the exact same pattern repeats over the
            second half of the bar: <em>3, the "a" of 3, and the "and" of 4</em>. So a
            fast tresillo is, in a sense, a slow tresillo played twice as fast and
            looped twice within a bar.
          </p>
          <p>
            This is dancier and more challenging. The hits are closer together, which
            asks more of your hand independence and your sense of placement. It's also
            where dembow and many modern reggaeton tracks live. Same rhythm DNA, more
            urgent feel.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[TRESILLO_FAST]} />

        <Prose>
          <p>
            For practice on the piano, try the same hand-independence drill: left hand
            on the pulse (1, 2, 3, 4 — straight), right hand on the fast tresillo. To
            make the bass even dancier, toggle the left hand between the root and the
            fifth of the chord — root on beats 1 and 3, fifth on beats 2 and 4 — while
            the right hand stays on the tresillo hits. That's a stock salsa bass
            pattern, and once it's in your hands you can apply it to any chord
            progression.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A modified tresillo" title="One hit shifted, a fresher feel">
        <Prose>
          <p>
            One useful variation breaks up the repetition without abandoning the
            pattern. The <strong>modified tresillo</strong> takes the standard slow
            shape — hits on 1, &-of-2, and 4 — and pulls the third hit back by one
            sixteenth, landing it on the <em>a</em> of beat 3 instead of squarely on
            beat 4. Same three-hit DNA, slightly more forward-leaning lurch.
          </p>
          <p>
            That single shift is the kind of small change that producers and
            arrangers use to keep a four-bar loop from feeling stale. Bar 1 plays the
            standard tresillo; bar 2 plays the modified version; loop. Suddenly the
            groove has internal motion without you ever changing tempo or pitch.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[TRESILLO_SLOW, TRESILLO_MODIFIED, TRESILLO_FAST, TRESILLO]} />
      </Chapter>

      <Chapter eyebrow="Putting it under your hands" title="Steps to internalise the pattern">
        <Steps>
          <Step>
            <strong>Clap and count first.</strong> Don't touch the piano yet. Set a
            metronome to 80 BPM. Count "1 and 2 and 3 and 4 and" out loud. Clap on 1,
            the <em>and</em>-of-2, and 4. Repeat for two minutes without losing the
            count.
          </Step>
          <Step>
            <strong>Move it onto the body.</strong> Tap your chest on the pulse (the
            kick) and tap your leg on the tresillo hits. Now your two hands are doing
            different things — exactly what they'll have to do at the keyboard.
          </Step>
          <Step>
            <strong>Single-note piano.</strong> Pick any note. Left hand plays the
            pulse, right hand plays the tresillo. Keep both hands on the same note for
            the first round to remove pitch from the equation.
          </Step>
          <Step>
            <strong>Add a scale.</strong> Right hand stays on the tresillo, but each
            hit moves up one note in your chosen scale. Try B Dorian (B C♯ D E F♯ G♯ A)
            for a Latin/jazz feel.
          </Step>
          <Step>
            <strong>Add chords.</strong> Left hand plays the bass note of a chord
            progression — say B minor, G major, D major, A major, one bar each. Right
            hand plays the tresillo using the chord tones above.
          </Step>
          <Step>
            <strong>Spend a week on the body.</strong> Forget the piano for a few days.
            Walk around the house tapping the tresillo on your thigh while counting out
            loud. The pattern needs to live in your nervous system before it lives in
            your fingers.
          </Step>
        </Steps>

        <TheoryBox label="The bass-line variant">
          The tresillo doesn't only ride in the right hand. Switch it to the left and
          you have a tresillo bass line, with the right hand free for chords or melody.
          One pattern, two roles. Try playing a B minor chord with your right hand on
          the pulse, and a B-C♯-D walking tresillo line in the left.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Where you'll hear it" title="The most ubiquitous rhythm of the last 15 years">
        <Prose>
          <p>
            Once you can play tresillo cleanly, you start hearing it everywhere. It's
            in <em>Despacito</em>, <em>Shape of You</em>, <em>One Dance</em>, almost
            every Drake hook from 2015 onward, most reggaeton, much of Afrobeat, and
            half the Spotify Top 50 in any given week. It became, quietly, the default
            rhythmic accent of global popular music.
          </p>
          <p>
            That's why this chapter sits where it does. Once you've understood the
            tresillo, you've understood the rhythmic skeleton of an enormous amount of
            modern music. The next chapter will build on it: tresillo is also the first
            half of the <em>son clave</em>, the foundational rhythmic motif of salsa,
            mambo, and Afro-Cuban jazz. Learn this pattern first, and the claves will
            make instant sense.
          </p>
        </Prose>

        <Glossary>
          <Term name="Tresillo" def="Spanish for 'triplet', from tres (three). A three-hit rhythmic pattern across four beats — hits on 1, the &-of-2, and 4 (slow version). The DNA of modern Latin and pop." />
          <Term name="Pulse" def="The underlying steady beat. The 'tick of the metronome'. Tresillo rides on top of a four-beat pulse." />
          <Term name="Subdivision" def="The smaller equal slots into which each beat is divided. Eighth-note subdivision = 'and'. Sixteenth-note subdivision = 'e and a'." />
          <Term name="3-3-2" def="The eighth-note distances between consecutive tresillo hits: 3 eighths from hit 1 to hit 2, 3 eighths from hit 2 to hit 3, 2 eighths to wrap back." />
          <Term name="Hand independence" def="The ability to play different rhythms with your left and right hands simultaneously. The core skill that tresillo practice builds." />
          <Term name="Modified tresillo" def="A variation in which the third hit is shifted from beat 4 to the 'a' of beat 3. Useful for breaking up repetitive loops." />
        </Glossary>

        <MTQuote>
          It can also be played like a bass line in the left hand, or as a chord pattern or
          an arpeggio, anything. So I would recommend first practice clapping it, doing it
          on the body, counting it out well, writing it down and then exploring the same on
          the piano.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/17a-tresillo-slow-and-fast.png"
          caption="Jason's note: the tresillo written two ways. Slow version counted in eighths (1 &amp; 2 &amp; 3 &amp; 4 &amp;), hits on 1, the &amp;-of-2 and 4. Fast version counted in sixteenths (1 e &amp; a 2 e &amp; a 3 e &amp; a 4 e &amp; a), hits on 1, the a-of-1 and the &amp;-of-2, repeating from beat 3. Either way it is the same 3+3+2 grouping that powers reggaeton, modern pop and most Drake-era hip-hop."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-18"
        label="Chapter 18 · Salsa Rhythm Patterns - Son, Rumba & Bossa Clave (Slow & Fast)"
        why="Tresillo is only the first bar. The next move is to add the answering second bar that turns it into a clave — the two-bar phrase at the heart of Latin music."
      />
    </LessonShell>
  );
}
